import { useEffect, useMemo, useState } from 'react';
import { useProfile, useCras, useTreasuries } from '@shared/state';
import { VAT_THRESHOLD_BIC, VAT_THRESHOLD_BNC } from '@shared/domain';

export type SmartAlertKind = 'runway' | 'vat' | 'late-invoice';

export interface SmartAlert {
  kind: SmartAlertKind;
  title: string;
  message: string;
  link?: string;
  linkLabel?: string;
}

const DISMISS_KEY_PREFIX = 'ix_smart_alert_dismissed_';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;
// Évènement custom déclenché à chaque `dismissAlert()`, pour que tout
// consommateur du même onglet (bannière, Hub) se resynchronise
// immédiatement — `storage` ne se déclenche que pour les *autres* onglets.
const DISMISS_EVENT = 'ix-smart-alert-dismiss';

export function dismissAlert(kind: SmartAlertKind): void {
  localStorage.setItem(`${DISMISS_KEY_PREFIX}${kind}`, String(Date.now()));
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(DISMISS_EVENT));
}

export function loadDismissedKinds(): Set<SmartAlertKind> {
  const kinds: SmartAlertKind[] = ['runway', 'vat', 'late-invoice'];
  const set = new Set<SmartAlertKind>();
  for (const kind of kinds) {
    const raw = localStorage.getItem(`${DISMISS_KEY_PREFIX}${kind}`);
    if (raw && Date.now() - Number(raw) < DISMISS_TTL_MS) set.add(kind);
  }
  return set;
}

/**
 * useDismissedAlertKinds — état réactif des types d'alerte masqués (LOT 2,
 * correctif MOB-002/Codex #4). `SmartAlertBanner` et `useHubBrief`
 * partagent ce hook plutôt que de relire `loadDismissedKinds()` chacun de
 * leur côté avec un état local isolé (l'ancien bug : la bannière et Mon Hub
 * désynchronisés) — la persistance/TTL reste celle de `dismissAlert`/
 * `loadDismissedKinds`, ce hook ne fait qu'exposer un état React qui se
 * resynchronise sur dismiss (même onglet) ou `storage` (autre onglet).
 */
export function useDismissedAlertKinds(): Set<SmartAlertKind> {
  const [dismissed, setDismissed] = useState<Set<SmartAlertKind>>(() => loadDismissedKinds());

  useEffect(() => {
    function refresh() {
      setDismissed(loadDismissedKinds());
    }
    window.addEventListener(DISMISS_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(DISMISS_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  return dismissed;
}

export function useSmartAlerts(): SmartAlert[] {
  const profile = useProfile();
  const cras = useCras();
  const treasuries = useTreasuries();

  return useMemo(() => {
    const alerts: SmartAlert[] = [];
    const now = new Date();

    // 1. Runway critique (< 3 mois) — priorité maximale
    for (const tr of treasuries) {
      const charges = tr.charges.reduce((s: number, c: { amount: number }) => s + c.amount, 0);
      if (charges > 0) {
        const runwayMonths = tr.openingBalance / charges;
        if (runwayMonths > 0 && runwayMonths < 3) {
          alerts.push({
            kind: 'runway',
            title: 'Réserve critique',
            message: `${runwayMonths.toFixed(1)} mois de trésorerie disponible. Pensez à anticiper.`,
            link: '#/tools/runway',
            linkLabel: 'Voir la réserve',
          });
          break; // une seule alerte runway suffit
        }
      }
    }

    // 2. Seuil TVA proche (≥ 70 % du seuil franchise)
    if (!alerts.some((a) => a.kind === 'runway') && profile?.status === 'ei' && !profile?.vatRegistered) {
      const caYtd = cras
        .filter((c) => c.year === now.getFullYear())
        .reduce((s, c) => s + (c.revenue || 0), 0);
      const isBicVente = profile?.eiCategory === 'bic-vente';
      const threshold = isBicVente ? VAT_THRESHOLD_BIC : VAT_THRESHOLD_BNC;
      if (caYtd / threshold >= 0.7 && caYtd > 0) {
        const remaining = Math.max(0, threshold - caYtd);
        alerts.push({
          kind: 'vat',
          title: 'Seuil TVA proche',
          message: `Il reste ${Math.round(remaining).toLocaleString('fr-FR')} € avant le seuil de franchise (${threshold.toLocaleString('fr-FR')} €).`,
          link: '#/tools/fiscal',
          linkLabel: 'Simuler',
        });
      }
    }

    // 3. Facture en retard (overdue explicite ou invoiced > 45 jours sans paiement)
    const MS_45 = 45 * 24 * 60 * 60 * 1000;
    const nowMs = Date.now();
    const lateCount = cras.filter(
      (c) => c.invoiceStatus === 'overdue' || (c.invoiceStatus === 'invoiced' && c.invoicedAt && nowMs - new Date(c.invoicedAt).getTime() > MS_45),
    ).length;
    if (lateCount > 0) {
      alerts.push({
        kind: 'late-invoice',
        title: `${lateCount} facture${lateCount > 1 ? 's' : ''} en retard`,
        message: `${lateCount > 1 ? 'Elles' : 'Elle'} demande${lateCount > 1 ? 'nt' : ''} votre attention.`,
        link: '#/tools/missio',
        linkLabel: 'Voir Missio',
      });
    }

    return alerts;
  }, [profile, cras, treasuries]);
}
